import React, {useRef, useMemo, Children, isValidElement} from 'react';
import type {ComponentProps, ReactNode} from 'react';
import {emotionStyled} from '@packages/utilities/styled';
import {
  View,
  HStack,
  VStack,
  FieldAttachment24Icon,
  Text,
} from '@packages/systems/spring';
import {TextareaSubmit} from './Submit';
import {TextareaActions} from './Actions';
import {TextareaProvider} from '../context';
import type {TextareaContext, TagData} from '../context';
import {useDynamicHeight} from '../hooks/useTextareaDynamicHeight';
import {useTextareaResizeObserver} from '../hooks/useTextareaResizeObserver';
import {
  MIN_HEIGHT,
  MAX_HEIGHT,
  TOOLBAR_HEIGHT,
  RESIZE_OBSERVER_PADDING,
} from '../constants';
import {useDragHover} from '../hooks';

const RootWrapper = emotionStyled(View)(
  {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: '$2',
    backgroundColor: '$uiInputBackground',
    border: '1px solid $uiInputBorderColor',
    boxShadow: '$inputInner',
    '&:hover': {
      borderColor: '$uiInputBorderColorHover',
    },
    '&:focus-within': {
      outline: 'none',
      borderColor: '$blueBorder',
    },
    '&[data-disabled="true"]': {
      cursor: 'not-allowed',
      borderColor: '$border3',
    },
  },
  {
    variants: {
      dragHover: {
        true: {
          border: '1px dashed $blueBorder',
          '&:hover': {
            borderColor: '$blueBorder',
          },
          '&:focus-within': {
            borderColor: '$blueBorder',
          },
        },
      },
    },
  }
);

const ContentWrapper = emotionStyled(View)(
  {
    padding: '$2',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'space-between',
  },
  {
    variants: {
      isDisabled: {
        true: {
          backgroundColor: '$backgroundDisabled',
          cursor: 'not-allowed',
          '& *': {
            cursor: 'not-allowed',
          },
          '& button, & button *': {
            cursor: 'pointer',
          },
          '& button[aria-disabled="true"], & button[aria-disabled="true"] *': {
            cursor: 'not-allowed',
          },
        },
      },
    },
  }
);

export interface RootProps extends Omit<
  ComponentProps<typeof RootWrapper>,
  'children'
> {
  minHeight?: number;
  maxHeight?: number;
  showDropzone?: boolean;
  tags?: TagData[];
  children: ReactNode;
  contextValue: TextareaContext;
  hideSubmitButton?: boolean;
  /** Optional content rendered above the input shell (e.g. a Spring `Note`). */
  note?: ReactNode;
  /** Callback function to add files to the textarea. */
  addFiles?: (files: File[]) => void | Promise<void>;
}

const isActionsComponent = (child: ReactNode): boolean => {
  return (
    isValidElement(child) &&
    typeof child.type === 'function' &&
    child.type === TextareaActions
  );
};

export const Textarea = ({
  tags,
  children,
  contextValue,
  showDropzone,
  hideSubmitButton,
  minHeight = MIN_HEIGHT,
  maxHeight = MAX_HEIGHT,
  note,
  addFiles,
  ...rest
}: RootProps) => {
  const tagsRef = useRef<HTMLDivElement>(null);
  const rootElementRef = React.useRef<HTMLDivElement>(null);
  const dragHover = useDragHover(rootElementRef);

  useDynamicHeight({
    tagsRef,
    tags: tags ?? [],
    minHeight,
    maxHeight,
    toolbarHeight: TOOLBAR_HEIGHT,
    textareaRef: contextValue.inputRef,
  });

  useTextareaResizeObserver({
    padding: RESIZE_OBSERVER_PADDING,
    textareaRef: contextValue.inputRef,
  });

  const ctx = useMemo(
    () => ({
      ...contextValue,
      tagsRef,
      tags,
    }),
    [contextValue, tags]
  );

  const isDisabled = contextValue.isDisabled || contextValue.isLoading;

  const {actionsContent, otherChildren} = useMemo(() => {
    let foundActionsContent: ReactNode = null;
    const foundOtherChildren: ReactNode[] = [];

    Children.forEach(children, (child) => {
      if (isActionsComponent(child)) {
        foundActionsContent = isValidElement(child)
          ? child.props.children
          : null;
      } else {
        foundOtherChildren.push(child);
      }
    });

    return {
      actionsContent: foundActionsContent,
      otherChildren: foundOtherChildren,
    };
  }, [children]);

  const onDrop = (event: React.DragEvent) => {
    if (!showDropzone || !addFiles) return;

    event.preventDefault();

    const files = Array.from(event.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      addFiles(files);
    }
  };

  const shell = (
    <RootWrapper
      ref={rootElementRef}
      width="100%"
      minHeight={minHeight}
      dragHover={dragHover}
      data-disabled={isDisabled}
      onDrop={onDrop}
      {...rest}
    >
      {showDropzone && addFiles ? (
        <VStack
          minHeight={minHeight}
          spacing={1}
          align="center"
          justify="center"
          pointerEvents="none"
          userSelect="none"
        >
          <FieldAttachment24Icon />
          <Text>Drop your image here</Text>
        </VStack>
      ) : (
        <ContentWrapper isDisabled={isDisabled}>
          {otherChildren}
          <HStack
            isFullWidth
            justify={actionsContent ? 'space-between' : 'end'}
          >
            {actionsContent && (
              <HStack spacing={2} minWidth={0} flex={1}>
                {actionsContent}
              </HStack>
            )}
            {!hideSubmitButton && <TextareaSubmit />}
          </HStack>
        </ContentWrapper>
      )}
    </RootWrapper>
  );

  return (
    <TextareaProvider value={ctx}>
      {note ? (
        <VStack spacing={2} width="100%" align="stretch">
          {note}
          {shell}
        </VStack>
      ) : (
        shell
      )}
    </TextareaProvider>
  );
};
